import { PageHeaderWrapper } from "@clipboard-health/ui-components";
import { getSafeAreaPaddings, getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useCredentialRequirementConfiguration } from "@src/appV2/redesign/Documents/useCredentialRequirementConfiguration";
import { ReferralShareLinkPageContent } from "@src/appV2/Referral/ShareLink/PageContent";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { transformPlacementData, useGetPlacement } from "../../api/useGetPlacement";
import { useHiringQualificationRanking } from "../../api/useHiringQualificationRanking";
import { usePlacementDetailReferralViewCountStorage } from "./usePlacementDetailReferralViewCount";
import { useSharePlacementReferral } from "./useSharePlacementReferral";

export function PlacementReferralPage() {
  const { placementId } = useParams<{
    placementId: string;
  }>();
  const worker = useDefinedWorker();

  const {
    data: placement,
    isLoading: isPlacementLoading,
    isSuccess: isPlacementSuccess,
  } = useGetPlacement(
    { placementId, workerId: worker.userId },
    { enabled: isDefined(placementId) && isDefined(worker.userId) }
  );
  const { showErrorToast } = useToast();

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  const placementDetails = isPlacementSuccess
    ? transformPlacementData(placement, credentialRequirementConfiguration)
    : undefined;

  const { getHighestRankingQualification } = useHiringQualificationRanking();

  const highestRankingSharedQualification = getHighestRankingQualification(
    (placementDetails?.workerTypes ?? []).filter((qualification) =>
      worker.licensesData.map((licenseData) => licenseData.qualification).includes(qualification)
    )
  );

  const { handleShareReferral, isLoadingReferralCode, maxReferralBonusAmount } =
    useSharePlacementReferral();

  const { setPlacementDetailsReferralViewCount } = usePlacementDetailReferralViewCountStorage();

  useEffect(() => {
    setPlacementDetailsReferralViewCount((previous) => previous + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper
        sx={(theme) => ({
          backgroundColor: theme.palette.common.white,
          ...getSafeAreaPaddings({
            bottom: true,
            fallbackBottom: theme.spacing(4),
          }),
        })}
      >
        <PageHeaderWrapper variant="tertiary">
          <PageHeaderTopRow />
        </PageHeaderWrapper>
        <ReferralShareLinkPageContent
          maxReferralBonusAmount={maxReferralBonusAmount}
          isLoading={isPlacementLoading || isLoadingReferralCode}
          onShareReferral={async () => {
            if (!isDefined(placementDetails)) {
              showErrorToast(
                "There was an error sharing your referral code, please refresh and try again."
              );
              return;
            }

            await handleShareReferral({
              placementId,
              payRange: placementDetails?.payRange,
              workplaceName: placementDetails?.workplace.name,
              qualificationDisplayLabel: highestRankingSharedQualification ?? "",
              // At the time of implementation, there are only 3 placements with null address.formatted
              formattedAddress: placementDetails?.address.formatted ?? "",
            });
          }}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
